/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import { format } from 'date-fns';
import noop from 'lodash/noop';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { USER_STATUS, inviteUser } from '../user';
import API from '../../api';
import { APIError, resetRequestState } from '../app';

export const REQUEST_STATUS = {
    ACTIVE: 'ACTIVE',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
};

const getTestersForReportsFilters = createAsyncThunk(
    'testers/getTestersForReportsFilters',
    async (onSuccess = noop) => {
        const response = await API.fetchTesters();

        const preparedData = response.map((tester) => ({
            label: tester.fullName,
            value: tester.id,
        }));
        const sortedByName = preparedData.sort((a, b) => a.label.localeCompare(b.label));

        if (onSuccess) onSuccess(sortedByName);
    },
);

const getTesters = createAsyncThunk('testers/getTesters', async (payload) => {
    const data = await API.fetchTestersList(payload);

    return data;
});

const createTester = createAsyncThunk(
    'testers/createNewTester',
    async ({ tester, onSuccess = noop, onError = noop }, thunkAPI) => {
        try {
            const payload = {
                ...tester,
                dob: format(tester.dob, 'MM-dd-yyyy'),
            };
            const data = await API.createTester(payload);

            onSuccess();
            return data;
        } catch (error) {
            onError(error);
            thunkAPI.dispatch(APIError({ text: 'Error creating tester.' }));
        }
    },
);

const updateTester = createAsyncThunk(
    'testers/updateTester',
    async ({ testerId, testerData, onSuccess = noop, onError = noop }, thunkApi) => {
        try {
            const payload = testerData;

            if (payload.dob) {
                payload.dob = format(payload.dob, 'MM-dd-yyyy');
            }

            // Set on the back-end
            delete payload.fullName;
            delete payload.age;
            delete payload.lastInvitedOn;
            delete payload.id;
            delete payload.requestStatus;

            const result = await API.updateInternalUser(testerId, payload);
            onSuccess();
            return result;
        } catch (error) {
            onError(error);
            thunkApi.dispatch(APIError({ text: 'Error updating tester.' }));
        }
    },
);

const activateTester = (testerId, onSuccess, onError) => async (dispatch) => {
    const patchData = {
        status: USER_STATUS.ACTIVE,
    };
    await updateTester({ testerId, testerData: patchData, onSuccess, onError })(dispatch);
};

const deactivateTester = (testerId, onSuccess, onError) => async (dispatch) => {
    const patchData = {
        status: USER_STATUS.INACTIVE,
    };
    await updateTester({ testerId, testerData: patchData, onSuccess, onError })(dispatch);
};

const deleteTester = createAsyncThunk(
    'testers/deleteTester',
    async ({ id, onSuccess = noop, onError = noop }, thunkApi) => {
        try {
            await API.deleteUser(id);
            onSuccess();
        } catch (error) {
            thunkApi.dispatch(APIError({ text: 'Error deleting user.' }));
            onError();
        }
    },
);

const initialState = {
    data: {},
    requestStatus: null,
    errorMsg: null,
    total: 0,
    requests: {
        getTesters: { loading: 'idle', requestId: null },
        createTester: { loading: 'idle', requestId: null },
        updateTester: { loading: 'idle', requestId: null },
        deleteTester: { loading: 'idle', requestId: null },
    },
};

const testersSlice = createSlice({
    name: 'testers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(resetRequestState, (state) => {
                state.errorMsg = initialState.errorMsg;
                state.requestStatus = initialState.requestStatus;
                state.requests = { ...initialState.requests };
            })
            .addCase(getTesters.pending, (state, action) => {
                state.requests.getTesters.loading = 'pending';
                state.requests.getTesters.requestId = action.meta.requestId;
            })
            .addCase(getTesters.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.requests.getTesters.loading === 'pending' && state.requests.getTesters.requestId === requestId) {
                    state.requests.getTesters.loading = 'idle';
                    state.requests.getTesters.requestId = null;
                    state.rawData = action.payload.results;
                    state.total = action.payload.total;
                }
            })
            .addCase(getTesters.rejected, (state, action) => {
                const { requestId } = action.meta;
                if (state.requests.getTesters.loading === 'pending' && state.requests.getTesters.requestId === requestId) {
                    state.requests.getTesters.loading = 'idle';
                    state.requests.getTesters.requestId = null;
                    state.errorMsg = action.error?.message || 'Error fetching testers.';
                }
            })
            .addCase(createTester.pending, (state, action) => {
                state.requests.createTester.loading = 'pending';
                state.requests.createTester.requestId = action.meta.requestId;
            })
            .addCase(createTester.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.requests.createTester.loading === 'pending' && state.requests.createTester.requestId === requestId) {
                    state.requests.createTester.loading = 'idle';
                    state.requests.createTester.requestId = null;
                    state.data = {
                        ...state.data,
                        [action.payload.id]: {
                            ...action.payload.data,
                            status: USER_STATUS.INVITED,
                            requestStatus: REQUEST_STATUS.SUCCESS,
                        },
                    };
                }
            })
            .addCase(createTester.rejected, (state, action) => {
                const { requestId, arg: { tester } = {} } = action.meta;
                if (state.requests.createTester.loading === 'pending' && state.requests.createTester.requestId === requestId) {
                    state.requests.createTester.loading = 'idle';
                    state.requests.createTester.requestId = null;
                    if (tester && tester.id) {
                        state.data[tester.id] = {
                            ...(state.data[tester.id] || {}),
                            requestStatus: REQUEST_STATUS.ERROR,
                            errorMsg: action.error?.message || 'Error creating tester.',
                        };
                    }
                }
            })
            .addCase(updateTester.pending, (state, action) => {
                state.requests.updateTester.loading = 'pending';
                state.requests.updateTester.requestId = action.meta.requestId;
            })
            .addCase(updateTester.fulfilled, (state, action) => {
                const { requestId, arg: { testerId } } = action.meta;
                if (state.requests.updateTester.loading === 'pending' && state.requests.updateTester.requestId === requestId) {
                    state.requests.updateTester.loading = 'idle';
                    state.requests.updateTester.requestId = null;
                    state.data = {
                        ...state.data,
                        [testerId]: {
                            ...action.payload,
                            requestStatus: REQUEST_STATUS.SUCCESS,
                        },
                    };
                }
            })
            .addCase(updateTester.rejected, (state, action) => {
                const { requestId, arg: { testerId } } = action.meta;
                if (state.requests.updateTester.loading === 'pending' && state.requests.updateTester.requestId === requestId) {
                    state.requests.updateTester.loading = 'idle';
                    state.requests.updateTester.requestId = null;
                    state.data = {
                        ...state.data,
                        [testerId]: {
                            ...state.data[testerId],
                            requestStatus: REQUEST_STATUS.ERROR,
                            errorMsg: action.payload?.errorMsg || action.error?.message || 'Error updating tester.',
                        },
                    };
                }
            })
            .addCase(deleteTester.pending, (state, action) => {
                state.requests.deleteTester.loading = 'pending';
                state.requests.deleteTester.requestId = action.meta.requestId;
            })
            .addCase(deleteTester.fulfilled, (state, action) => {
                const { requestId, arg: { id } = {} } = action.meta;
                if (state.requests.deleteTester.loading === 'pending' && state.requests.deleteTester.requestId === requestId) {
                    state.requests.deleteTester.loading = 'idle';
                    state.requests.deleteTester.requestId = null;
                    const data = { ...state.data };
                    if (id) delete data[id];
                    state.data = { ...data };
                }
            })
            .addCase(deleteTester.rejected, (state, action) => {
                const { requestId, arg: { id } = {} } = action.meta;
                if (state.requests.deleteTester.loading === 'pending' && state.requests.deleteTester.requestId === requestId) {
                    state.requests.deleteTester.loading = 'idle';
                    state.requests.deleteTester.requestId = null;
                    if (id && state.data[id]) {
                        state.data[id] = {
                            ...state.data[id],
                            requestStatus: REQUEST_STATUS.ERROR,
                            errorMsg: action.error?.message || 'Error deleting tester.',
                        };
                    }
                }
            })
            .addCase(inviteUser.fulfilled, (state, action) => {
                if (state.data[action.payload?.userId]) {
                    state = {
                        ...state,
                        data: {
                            ...state.data,
                            [action.payload.userId]: {
                                ...state.data[action.payload.userId],
                                lastInvitedOn: new Date(),
                            },
                        },
                    };
                }
            });
    },
});

const { reducer } = testersSlice;

export {
    getTestersForReportsFilters,
    getTesters,
    createTester,
    updateTester,
    deleteTester,
    activateTester,
    deactivateTester,
};

export default reducer;
